/**
 * Modal de escaneo del acta de recepción.
 *
 * Cada lectura del escáner (o una serie tipeada) se interpreta
 * (`interpretarLectura`) y se manda al back, que la ubica entre las series
 * que ANMAT declaró para el acta: la marca como recibida, o la registra como
 * SOBRANTE si el emisor no la informó. El modal muestra la última lectura con
 * su resultado y las unidades ya marcadas, la más reciente arriba.
 *
 * Reutiliza `CampoLecturaGs1` (captura con todas las correcciones de lector)
 * y registra cada lectura en el log de diagnóstico del pickeo.
 */

import { useMemo, useRef, useState } from 'react';
import { CheckCircle2, Loader2, Trash2, Volume2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { toast } from '@/lib/notify';
import { playScanSound, probarSonido } from '@/lib/audio';
import { fmtDate } from '@/lib/format';
import { ApiError } from '@/api/http';
import { CampoLecturaGs1, type MetaLectura } from '@/components/lector/CampoLecturaGs1';
import { lectorEnRafaga } from '@/components/lector/rafaga-lector';
import { obtenerEntornoLector } from '@/components/lector/entorno-lector';
import {
  useDesmarcarSerieMutation,
  useMarcarSerieMutation,
  useRegistrarLecturaPickeoMutation,
} from '@/api/queries';
import type { ActaRecepcion, SerieActa } from '@/api/contracts/recepcion-acta';
import { interpretarLectura } from './acta-helpers';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  acta: ActaRecepcion;
}

type TipoResultado = 'marcada' | 'sobrante' | 'rechazada' | 'ignorada';

interface UltimaLectura {
  tipo: TipoResultado;
  titulo: string;
  mensaje: string | null;
  serie: string | null;
}

const ESTILO_RESULTADO: Record<TipoResultado, string> = {
  marcada: 'border-green-200 bg-green-50 text-green-900',
  sobrante: 'border-amber-300 bg-amber-50 text-amber-900',
  rechazada: 'border-red-200 bg-red-50 text-red-900',
  ignorada: 'border-gray-200 bg-gray-50 text-gray-700',
};

export function EscaneoActaModal({ open, onOpenChange, acta }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [ultima, setUltima] = useState<UltimaLectura | null>(null);
  const [aQuitar, setAQuitar] = useState<SerieActa | null>(null);

  const marcar = useMarcarSerieMutation(acta.id);
  const desmarcar = useDesmarcarSerieMutation(acta.id);
  const { mutate: registrarLectura } = useRegistrarLecturaPickeoMutation();

  const recibibles = acta.totales.total - acta.totales.anulada;
  const recibidas = acta.totales.marcada + acta.totales.trazado;

  // Más reciente arriba: el operador ve la última unidad sin desplazarse.
  const marcadas = useMemo(
    () =>
      acta.series
        .filter((s) => s.estado === 'marcada')
        .sort((a, b) => (b.marcadaAt ?? '').localeCompare(a.marcadaAt ?? '')),
    [acta.series],
  );

  const loguear = (
    raw: string,
    meta: MetaLectura,
    resultado: string,
    m: { gtin?: string | null; lote?: string | null; serie?: string | null },
  ) => {
    void obtenerEntornoLector()
      .catch(() => null)
      .then((entorno) =>
        registrarLectura({
          referencia: acta.numeroRemito ?? acta.numeroFactura ?? `acta-${acta.id}`,
          texto: raw.slice(0, 4000),
          resultado: `recepcion:${resultado}`,
          gtin: m.gtin ?? null,
          lote: m.lote ?? null,
          serie: m.serie ?? null,
          duracionMs: meta.duracionMs,
          teclas: meta.teclas,
          entorno,
        }),
      );
  };

  const onLectura = (raw: string, meta: MetaLectura) => {
    const lectura = interpretarLectura(raw);
    if (!lectura.ok) {
      if (lectura.motivo !== 'vacia') {
        playScanSound('error');
        setUltima({
          tipo: 'ignorada',
          titulo: 'Lectura sin serie',
          mensaje: lectura.mensaje,
          serie: null,
        });
        loguear(raw, meta, lectura.motivo, {});
      }
      return;
    }
    const { marca } = lectura;
    marcar.mutate(marca, {
      onSuccess: (res) => {
        if (res.resultado === 'marcada') {
          playScanSound('success');
          const loteDistinto = res.advertencias.includes('lote_distinto');
          setUltima({
            tipo: 'marcada',
            titulo: `Recibida · ${res.serie?.nombre ?? res.serie?.gtin ?? ''}`.trim(),
            mensaje: loteDistinto
              ? `El lote leído (${marca.lote ?? '—'}) no coincide con el que declaró el emisor (${res.serie?.lote ?? '—'}). Se marcó igual; revisá la etiqueta.`
              : null,
            serie: res.serie?.numeroSerial ?? marca.serie,
          });
          loguear(raw, meta, loteDistinto ? 'marcada_lote_distinto' : 'marcada', marca);
        } else {
          playScanSound('error');
          setUltima({
            tipo: 'sobrante',
            titulo: 'No declarada por el emisor',
            mensaje:
              'ANMAT no lista esta unidad para este documento. Quedó registrada como sobrante: no se puede confirmar ni entra al stock hasta que el emisor la informe.',
            serie: res.sobrante?.numeroSerial ?? marca.serie,
          });
          loguear(raw, meta, 'sobrante', marca);
        }
      },
      onError: (err) => {
        playScanSound('error');
        const code = err instanceof ApiError ? err.code : null;
        setUltima({
          tipo: 'rechazada',
          titulo: TITULO_ERROR[code ?? ''] ?? 'No se pudo marcar',
          mensaje: err.message,
          serie: marca.serie,
        });
        loguear(raw, meta, `error:${code ?? 'desconocido'}`, marca);
      },
    });
  };

  const probarAudio = async () => {
    const ok = await probarSonido();
    if (!ok) {
      toast.warning(
        'El navegador no pudo reproducir sonido. Revisá parlantes, volumen y que la pestaña no esté silenciada.',
      );
    }
  };

  const quitar = (s: SerieActa) => {
    desmarcar.mutate(s.id, {
      onSuccess: () => {
        setAQuitar(null);
        inputRef.current?.focus();
      },
      onError: (err) => toast.error(err.message),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          inputRef.current?.focus();
        }}
        // Radix escucha el Escape en captura sobre el documento. No hay que
        // cerrar el modal si el Escape viene del lector (la Syble lo manda en
        // lugar del GS) ni si el operador tiene texto en el campo.
        onEscapeKeyDown={(e) => {
          if (lectorEnRafaga() || (inputRef.current?.value ?? '') !== '') e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2 text-everex-azul">
            Recepción por escaneo
            {acta.numeroRemito && <span className="font-mono">{acta.numeroRemito}</span>}
            <span
              className={cn(
                'badge ml-auto font-normal',
                recibidas >= recibibles && recibibles > 0 ? 'badge-green' : 'badge-blue',
              )}
            >
              Recibidas {recibidas} de {recibibles}
            </span>
          </DialogTitle>
          <DialogDescription className="text-xs">
            Escaneá el DataMatrix de cada unidad, o tipeá el N° de serie y Enter. Lo que el emisor
            no declaró queda como sobrante.
            {acta.razonSocialOrigen && (
              <span className="text-gray-500"> · {acta.razonSocialOrigen}</span>
            )}
          </DialogDescription>
        </DialogHeader>

        <CampoLecturaGs1
          inputRef={inputRef}
          onLectura={onLectura}
          resetKey={open}
          disabled={acta.estado !== 'pendiente'}
        />

        {ultima && (
          <div className={cn('rounded-md border p-2 text-xs', ESTILO_RESULTADO[ultima.tipo])}>
            <div className="flex items-center gap-2">
              {ultima.tipo === 'marcada' && <CheckCircle2 className="h-4 w-4" />}
              <b>{ultima.titulo}</b>
              {ultima.serie && <span className="font-mono">{ultima.serie}</span>}
            </div>
            {ultima.mensaje && <p className="mt-1">{ultima.mensaje}</p>}
          </div>
        )}

        <div
          className="max-h-[36vh] overflow-y-auto rounded-md border border-gray-200"
          onMouseDown={(e) => {
            if ((e.target as HTMLElement).closest('tbody')) e.preventDefault();
          }}
        >
          {marcadas.length === 0 ? (
            <p className="px-4 py-8 text-center text-xs text-gray-500">
              Todavía no se marcó ninguna unidad en esta sesión. Escaneá la primera.
            </p>
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-gray-50 text-[10px] uppercase text-gray-600">
                <tr>
                  <th className="px-3 py-1.5 text-left">Producto</th>
                  <th className="px-3 py-1.5 text-left">GTIN</th>
                  <th className="px-3 py-1.5 text-left">Lote</th>
                  <th className="px-3 py-1.5 text-left">Vto.</th>
                  <th className="px-3 py-1.5 text-left">N° de serie</th>
                  <th className="px-3 py-1.5 text-left">Vía</th>
                  <th className="px-3 py-1.5" aria-label="Acciones" />
                </tr>
              </thead>
              <tbody>
                {marcadas.map((s, i) => (
                  <tr
                    key={s.id}
                    className={cn(
                      'border-t border-gray-100',
                      i === 0 && ultima?.tipo === 'marcada' && 'bg-green-50 transition-colors',
                    )}
                  >
                    <td className="px-3 py-1.5">{s.nombre ?? '—'}</td>
                    <td className="px-3 py-1.5 font-mono text-gray-600">{s.gtin ?? '—'}</td>
                    <td className="px-3 py-1.5 font-mono">{s.lote ?? '—'}</td>
                    <td className="px-3 py-1.5">{fmtDate(s.vencimiento)}</td>
                    <td className="px-3 py-1.5 font-mono font-semibold">{s.numeroSerial}</td>
                    <td className="px-3 py-1.5 text-gray-500">{s.marcadaVia ?? '—'}</td>
                    <td className="px-3 py-1.5 text-right">
                      {acta.estado === 'pendiente' && (
                        <button
                          type="button"
                          className="rounded text-red-600 hover:text-red-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-300"
                          title={`Desmarcar la serie ${s.numeroSerial ?? ''}`}
                          aria-label={`Desmarcar la serie ${s.numeroSerial ?? ''}`}
                          onClick={() => setAQuitar(s)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="flex items-center justify-between">
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => void probarAudio()}
            className="inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-everex-azul"
            title="Reproduce los sonidos de lectura correcta e incorrecta"
          >
            <Volume2 className="h-3.5 w-3.5" /> Probar sonido
          </button>
          <div className="flex items-center gap-2">
            {marcar.isPending && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Listo
            </Button>
          </div>
        </div>
      </DialogContent>

      {aQuitar && (
        <Dialog open onOpenChange={(o) => !o && setAQuitar(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-everex-azul">Desmarcar serie</DialogTitle>
              <DialogDescription className="text-xs">
                La serie <span className="font-mono">{aQuitar.numeroSerial}</span> vuelve a "sin
                marcar". ¿Confirmás?
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setAQuitar(null)}
                disabled={desmarcar.isPending}
              >
                Cancelar
              </Button>
              <Button
                onClick={() => quitar(aQuitar)}
                disabled={desmarcar.isPending}
                className="bg-red-600 text-white hover:bg-red-700"
              >
                Desmarcar
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
}

const TITULO_ERROR: Record<string, string> = {
  SERIE_YA_MARCADA: 'Ya estaba marcada',
  SERIE_YA_CONFIRMADA: 'Ya confirmada en ANMAT',
  SERIE_CON_ERROR: 'Rechazada por ANMAT',
  SERIE_NO_RECIBIDA: 'Estaba como no recibida',
  SERIE_ANULADA_EMISOR: 'Anulada por el emisor',
  SERIE_AMBIGUA: 'Serie repetida en el acta',
  SOBRANTE_DUPLICADO: 'Sobrante ya registrado',
  ACTA_CONFIRMANDO: 'Confirmación en curso',
  ACTA_CERRADA: 'Acta cerrada',
};
